import React, {
  useState,
  useEffect,
  useCallback,
  useImperativeHandle,
  forwardRef,
  useMemo,
  useRef,
} from 'react';
import {
  getPatientDocuments,
  getPatientDocumentUrl,
  updatePatientDocument,
  updateIntakeDocument,
  deleteIntakeDocument,
  deletePatientDocument,
  getIntakes,
  getReferralDocs,
} from '../../services/api';
import { useStore } from '../../store';
import PDFViewerNative from '../PDFViewerNative';
import type {
  PatientDocumentResponse,
  IntakeDocumentResponse,
  TaskListResponse,
} from '../../services/api/types.gen';
import { useToast } from '../../hooks/useToastContext';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@roger/ui-components';
import { formatDateToMMDDYYYY } from '../../utils/dateUtils';

interface IntakeDocumentWithMeta extends IntakeDocumentResponse {
  intakeId: string;
}

// Human-readable label for the `task_type` polymorphic discriminator
// (values: "visit", "order", "patient_communication", "misc_task",
// "document_upload", "task"). An unmapped value falls back to the raw string.
const TASK_TYPE_LABELS: Record<string, string> = {
  visit: 'Visit',
  order: 'Order',
  patient_communication: 'Communication',
  misc_task: 'Misc task',
  document_upload: 'Document upload',
  task: 'Task',
};

const formatTaskTag = (doc: PatientDocumentResponse): string | null => {
  if (!doc.task_id) return null;
  const typeLabel = doc.task_type
    ? (TASK_TYPE_LABELS[doc.task_type] ?? doc.task_type)
    : 'Task';
  return doc.task_scheduled_date
    ? `[${typeLabel} ${doc.task_scheduled_date}]`
    : `[${typeLabel}]`;
};

const LoadingSpinner: React.FC<{ size?: 'sm' | 'md'; text?: string }> = ({
  size = 'md',
  text,
}) => (
  <div className='flex items-center gap-2'>
    <div
      className={`animate-spin rounded-full border-2 border-primary border-t-transparent ${size === 'sm' ? 'h-4 w-4' : 'h-8 w-8'}`}
    />
    {text && <span className='text-sm text-muted-foreground'>{text}</span>}
  </div>
);

export interface PatientDocumentsTabHandle {
  refreshDocuments: () => void;
}

const ALL_DOCUMENTS_SCOPE = '__all__';

interface PatientDocumentsTabProps {
  patientId: string;
  /** Patient name for the upload toast target (e.g. "David Moore"). */
  patientLabel?: string;
  /** Visits, orders, and other tasks that support attachments (exclude communications). */
  documentContextTasks?: TaskListResponse[];
}

const PatientDocumentsTab = forwardRef<
  PatientDocumentsTabHandle,
  PatientDocumentsTabProps
>(({ patientId, patientLabel, documentContextTasks = [] }, ref) => {
  const { success: showSuccess, error: showError } = useToast();

  // Background uploads live in the store so the persistent toast + refresh
  // survive this tab (and its PDF viewer) unmounting when the user navigates.
  const startTaskDocumentUpload = useStore(
    state => state.startTaskDocumentUpload
  );
  const startPatientDocumentUpload = useStore(
    state => state.startPatientDocumentUpload
  );

  /** null = all documents (default); otherwise filter/uploads attach to this task */
  const [documentScopeTaskId, setDocumentScopeTaskId] = useState<string | null>(
    null
  );

  const [patientDocuments, setPatientDocuments] = useState<
    PatientDocumentResponse[]
  >([]);
  const [intakeDocuments, setIntakeDocuments] = useState<
    IntakeDocumentWithMeta[]
  >([]);
  const [patientDocsLoading, setPatientDocsLoading] = useState(false);
  const [intakeDocsLoading, setIntakeDocsLoading] = useState(false);
  const [activeDocumentId, setActiveDocumentId] = useState<string>('');
  const [documentUrls, setDocumentUrls] = useState<Record<string, string>>({});
  // ── Fetch patient documents ──────────────────────────────────────

  const fetchPatientDocuments = useCallback(async () => {
    setPatientDocsLoading(true);
    try {
      const response = await getPatientDocuments({
        path: { patient_id: patientId },
        query: { include_task_documents: true },
      });
      if (response?.data) {
        // Exclude intake-type docs — those are already shown via the intake relationship
        const docs = response.data.filter(d => d.document_type !== 'intake');
        setPatientDocuments(docs);
        const urlMap: Record<string, string> = {};
        // TODO: If this list grows large, switch to lazy URL signing (on access)
        // and add pagination so we do not eagerly fetch every document URL upfront.
        for (const doc of docs) {
          try {
            const urlResponse = await getPatientDocumentUrl({
              path: { patient_id: patientId, document_id: doc.id },
            });
            const urlData = urlResponse?.data as { url?: string } | undefined;
            if (urlData?.url) {
              urlMap[doc.id] = urlData.url;
            }
          } catch (err) {
            console.error(`Error fetching URL for document ${doc.id}:`, err);
            showError(
              'Document Error',
              'There was an error fetching the document.'
            );
          }
        }
        setDocumentUrls(prev => ({ ...prev, ...urlMap }));
      }
    } catch (err) {
      console.error('Failed to load patient documents:', err);
    } finally {
      setPatientDocsLoading(false);
    }
  }, [patientId]);

  useImperativeHandle(ref, () => ({
    refreshDocuments: fetchPatientDocuments,
  }));

  useEffect(() => {
    fetchPatientDocuments();
  }, [fetchPatientDocuments]);

  // ── Refresh after a background upload completes ──────────────────
  // The upload lives in the store (so it survives this tab unmounting); when it
  // succeeds it bumps a per-patient / per-task tick. Re-fetch on a bump so the
  // new document and count appear — including when the user navigated away and
  // came back mid-upload. The scoped task tick covers task uploads that bump only
  // the task tick (no patientId). Switching the scope dropdown doesn't *need* a
  // refetch — filteredDocuments re-filters the already-loaded set client-side —
  // though switching to/from a task whose tick already bumped this session still
  // fires one harmless idempotent refetch (its observed tick value changes).
  //
  // Ticks are normalized to 0 (their absent value) and the refs seed at 0 so the
  // very first upload (0 → 1) triggers a refetch. Seeding at undefined would swallow
  // that first bump, leaving the list stale until a second upload. Mirror of the
  // patientUploadTick watcher in PatientDetail.tsx.
  const patientUploadTick = useStore(
    state => state.patientUploadTick[patientId] ?? 0
  );
  const scopedTaskUploadTick = useStore(state =>
    documentScopeTaskId ? (state.taskUploadTick[documentScopeTaskId] ?? 0) : 0
  );
  const prevPatientTickRef = useRef(0);
  const prevTaskTickRef = useRef(0);
  useEffect(() => {
    const patientBumped = patientUploadTick !== prevPatientTickRef.current;
    const taskBumped = scopedTaskUploadTick !== prevTaskTickRef.current;
    if (patientBumped || taskBumped) {
      fetchPatientDocuments();
    }
    prevPatientTickRef.current = patientUploadTick;
    prevTaskTickRef.current = scopedTaskUploadTick;
  }, [patientUploadTick, scopedTaskUploadTick, fetchPatientDocuments]);

  // ── Fetch intake documents ───────────────────────────────────────

  useEffect(() => {
    setIntakeDocsLoading(true);
    getIntakes({
      query: { patient_id: patientId },
    })
      .then(async response => {
        if (response?.data?.items) {
          const allIntakeDocs: IntakeDocumentWithMeta[] = [];
          const urlMap: Record<string, string> = {};

          for (const intake of response.data.items) {
            if (intake.documents && intake.documents.length > 0) {
              // Include intakeId for URL refresh to work correctly
              const docsWithMeta = intake.documents.map(doc => ({
                ...doc,
                intakeId: intake.id,
              }));
              allIntakeDocs.push(...docsWithMeta);

              try {
                const docsResponse = await getReferralDocs({
                  path: { intake_id: intake.id },
                });
                if (docsResponse?.data) {
                  const docsData = docsResponse.data as {
                    documents?: Array<{ id: string; url: string }>;
                  };
                  if (docsData.documents) {
                    for (const docUrl of docsData.documents) {
                      urlMap[docUrl.id] = docUrl.url;
                    }
                  }
                }
              } catch (err) {
                console.error(
                  `Error fetching URLs for intake ${intake.id}:`,
                  err
                );
              }
            }
          }

          setIntakeDocuments(allIntakeDocs);
          setDocumentUrls(prev => ({ ...prev, ...urlMap }));
        }
      })
      .catch(err => console.error('Failed to load intake documents:', err))
      .finally(() => setIntakeDocsLoading(false));
  }, [patientId]);

  const sortedContextTasks = useMemo(() => {
    return [...documentContextTasks].sort(
      (a, b) =>
        new Date(b.task_date ?? b.scheduled_date).getTime() -
        new Date(a.task_date ?? a.scheduled_date).getTime()
    );
  }, [documentContextTasks]);

  useEffect(() => {
    if (
      documentScopeTaskId &&
      !sortedContextTasks.some(t => t.id === documentScopeTaskId)
    ) {
      setDocumentScopeTaskId(null);
    }
  }, [documentScopeTaskId, sortedContextTasks]);

  const filteredPatientDocuments = useMemo(() => {
    if (!documentScopeTaskId) {
      // "All documents": show every patient document, including those attached to tasks.
      return patientDocuments;
    }
    return patientDocuments.filter(d => d.task_id === documentScopeTaskId);
  }, [patientDocuments, documentScopeTaskId]);

  const displayedIntakeDocuments = useMemo(() => {
    if (documentScopeTaskId) return [];
    return intakeDocuments;
  }, [intakeDocuments, documentScopeTaskId]);

  // ── Keep selection valid when scope or data changes ──────────────

  useEffect(() => {
    const allDocs = [...displayedIntakeDocuments, ...filteredPatientDocuments];
    if (allDocs.length === 0) {
      setActiveDocumentId('');
      return;
    }
    if (!activeDocumentId || !allDocs.some(d => d.id === activeDocumentId)) {
      setActiveDocumentId(allDocs[0].id);
    }
  }, [displayedIntakeDocuments, filteredPatientDocuments, activeDocumentId]);

  // ── Handlers ─────────────────────────────────────────────────────

  const handleDocumentRename = async (documentId: string, fileName: string) => {
    const isPatientDoc = patientDocuments.some(d => d.id === documentId);

    try {
      if (isPatientDoc) {
        await updatePatientDocument({
          path: { patient_id: patientId, document_id: documentId },
          body: { file_name: fileName },
          throwOnError: true,
        });
        setPatientDocuments(prev =>
          prev.map(doc =>
            doc.id === documentId ? { ...doc, file_name: fileName } : doc
          )
        );
      } else {
        await updateIntakeDocument({
          path: { document_id: documentId },
          body: { file_name: fileName },
          throwOnError: true,
        });
        setIntakeDocuments(prev =>
          prev.map(doc =>
            doc.id === documentId ? { ...doc, file_name: fileName } : doc
          )
        );
      }
      showSuccess('Document Renamed', 'Document renamed successfully.');
    } catch (err) {
      console.error('Error renaming document:', err);
      showError('Rename Failed', 'Failed to rename document.');
    }
  };

  const handleDocumentAdd = async (file: File, displayName: string) => {
    // Hand the upload to the background store: the persistent toast and the
    // list/count refresh (via the tick effect below) survive this tab and its
    // PDF viewer unmounting when the user navigates away mid-upload. The viewer
    // shows an optimistic "Uploading…" preview meanwhile; on success we return
    // the persisted doc so the viewer swaps the temp doc for it by id AND keeps
    // it focused. On failure/cancel the store returns null, so we throw and the
    // viewer drops its stale preview — the uploader's toast carries the message.
    const patientTarget = patientLabel ?? 'this patient';

    // Route by the scope id itself, not by whether the task is still in the
    // context list. Falling back to patient scope just because the lookup missed
    // would silently misroute a task-scoped upload; the lookup now only supplies
    // the human-readable label.
    let doc: PatientDocumentResponse | null;
    if (documentScopeTaskId) {
      const scopedTask = sortedContextTasks.find(
        t => t.id === documentScopeTaskId
      );
      const label = scopedTask
        ? `${scopedTask.display_name} · ${patientTarget}`
        : patientTarget;
      doc = await startTaskDocumentUpload({
        taskId: documentScopeTaskId,
        patientId,
        file,
        displayName,
        targetLabel: label,
      });
    } else {
      doc = await startPatientDocumentUpload({
        patientId,
        file,
        displayName,
        targetLabel: patientTarget,
      });
    }

    if (!doc) {
      throw new Error('upload failed or canceled');
    }
    return {
      id: doc.id,
      fileName: doc.file_name,
      mimeType: doc.mime_type ?? undefined,
    };
  };

  const handleDocumentDelete = async (documentId: string): Promise<void> => {
    const isPatientDoc = patientDocuments.some(d => d.id === documentId);
    try {
      if (isPatientDoc) {
        await deletePatientDocument({
          path: { patient_id: patientId, document_id: documentId },
          throwOnError: true,
        });
        setPatientDocuments(prev => prev.filter(d => d.id !== documentId));
      } else {
        await deleteIntakeDocument({
          path: { document_id: documentId },
          throwOnError: true,
        });
        setIntakeDocuments(prev => prev.filter(d => d.id !== documentId));
      }
      if (activeDocumentId === documentId) {
        setActiveDocumentId('');
      }
      setDocumentUrls(prev => {
        const next = { ...prev };
        delete next[documentId];
        return next;
      });
      showSuccess('Document Deleted', 'Document deleted successfully.');
    } catch (err) {
      console.error('Error deleting document:', err);
      showError(
        'Delete Failed',
        'Failed to delete document. Please try again.'
      );
    }
  };

  const handleRefreshDocumentUrl = useCallback(
    async (documentId: string): Promise<string | null> => {
      // Check if this is an intake document
      const intakeDoc = intakeDocuments.find(d => d.id === documentId);

      try {
        if (intakeDoc) {
          // Intake document - use getReferralDocs API
          const docsResponse = await getReferralDocs({
            path: { intake_id: intakeDoc.intakeId },
          });
          const docsData = docsResponse.data as {
            documents?: Array<{ id: string; url: string }>;
          };
          if (docsData?.documents) {
            const doc = docsData.documents.find(d => d.id === documentId);
            if (doc?.url) {
              setDocumentUrls(prev => ({ ...prev, [documentId]: doc.url }));
              return doc.url;
            }
          }
          return null;
        } else {
          // Patient document - use getPatientDocumentUrl API
          const urlResponse = await getPatientDocumentUrl({
            path: { patient_id: patientId, document_id: documentId },
          });
          const urlData = urlResponse?.data as { url?: string } | undefined;
          if (urlData?.url) {
            setDocumentUrls(prev => ({ ...prev, [documentId]: urlData.url! }));
            return urlData.url;
          }
          return null;
        }
      } catch (err) {
        console.error(`Error refreshing URL for document ${documentId}:`, err);
        return null;
      }
    },
    [patientId, intakeDocuments]
  );

  // ── Render ───────────────────────────────────────────────────────

  return (
    <div className='overflow-hidden rounded-lg border bg-card'>
      <div className='flex flex-col gap-2 px-4 py-3 border-b bg-muted/30'>
        <div className='flex flex-wrap items-center gap-2'>
          {sortedContextTasks.length > 0 ? (
            <>
              <Select
                value={documentScopeTaskId ?? ALL_DOCUMENTS_SCOPE}
                onValueChange={v =>
                  setDocumentScopeTaskId(v === ALL_DOCUMENTS_SCOPE ? null : v)
                }
              >
                <SelectTrigger selectSize='sm' className='w-[min(100%,360px)]'>
                  <SelectValue placeholder='Document scope' />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL_DOCUMENTS_SCOPE}>
                    All documents
                  </SelectItem>
                  {sortedContextTasks.map(task => (
                    <SelectItem key={task.id} value={task.id}>
                      {task.display_name} -{' '}
                      {formatDateToMMDDYYYY(
                        task.task_date ?? task.scheduled_date
                      ) || 'N/A'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {documentScopeTaskId ? (
                <button
                  type='button'
                  onClick={() => setDocumentScopeTaskId(null)}
                  className='text-xs text-muted-foreground underline hover:text-foreground'
                >
                  Clear
                </button>
              ) : null}
            </>
          ) : null}
          {(intakeDocsLoading || patientDocsLoading) && (
            <LoadingSpinner size='sm' text='Loading documents...' />
          )}
        </div>
        {documentScopeTaskId ? (
          <p className='text-xs text-muted-foreground'>
            Showing documents attached to this task. Uploads add files to this
            task.
          </p>
        ) : sortedContextTasks.length > 0 ? (
          <p className='text-xs text-muted-foreground'>
            All documents for this patient, including attachments on tasks.
            Select a task to filter the list or upload only to that task.
          </p>
        ) : null}
      </div>
      <div>
        <div className='h-[calc(100vh-12rem)]'>
          <PDFViewerNative
            documents={[
              ...displayedIntakeDocuments.map(doc => ({
                id: doc.id,
                fileName: doc.file_name,
                mimeType: doc.mime_type ?? null,
                tooltipText: doc.task_type_tag
                  ? `[Intake · ${doc.task_type_tag}] ${doc.file_name}`
                  : `[Intake] ${doc.file_name}`,
                isPersisted: true,
                usedInExtraction: doc.used_in_extraction ?? true,
              })),
              ...filteredPatientDocuments.map(doc => {
                const taskTag = formatTaskTag(doc);
                return {
                  id: doc.id,
                  fileName: doc.file_name,
                  mimeType: doc.mime_type ?? null,
                  tooltipText: taskTag
                    ? `${taskTag} ${doc.file_name}`
                    : undefined,
                  isPersisted: true,
                  usedInExtraction: true,
                  canDelete: true,
                };
              }),
            ]}
            documentUrls={documentUrls}
            activeDocumentId={activeDocumentId}
            onActiveDocumentChange={setActiveDocumentId}
            onDocumentRename={handleDocumentRename}
            onDocumentAdd={handleDocumentAdd}
            onDocumentDelete={handleDocumentDelete}
            onRefreshUrl={handleRefreshDocumentUrl}
          />
        </div>
      </div>
    </div>
  );
});

PatientDocumentsTab.displayName = 'PatientDocumentsTab';

export default PatientDocumentsTab;
